import { Signal } from "../../core/Signal";
import type { BeatStep, ProceduralBeat } from "../ProceduralBeat";
import { SCALES } from "../dubstep/MusicalScale";
import type {
  BarContext,
  ChordQuality,
  ChordSpec,
  MotifNote,
  ResolvedChord,
  SectionKind,
  SectionStartEvent,
  Song,
  SongSection,
} from "./types";

// Composer = the brain of the music system. It turns a declarative
// `Song` (chord progression, lead motifs, section plan) into per-bar
// events that every synth layer subscribes to.
//
// Wire-up:
//   ProceduralBeat.onStep --> Composer (advances bar / section state)
//   Composer.onBar(BarContext)            -> SubBass / WobbleBass / Pad / Stabs
//   Composer.onSectionStart(SectionKind)  -> RiserFX / ImpactFX / drums switch
//   Composer.notesForStep(globalStep)     -> LeadSynth pulls notes per step
//
// The Composer does NOT touch the AudioContext. It stays pure logic so
// it can be unit-tested without audio support, and so layers stay free
// to pick their own synthesis.

// Each section's intrinsic dynamic energy. Drives a global gain
// envelope so even at peak combo the intro is quieter than the drop.
const SECTION_ENERGY: Record<SectionKind, number> = {
  intro: 0.35,
  verse: 0.55,
  build: 0.7,
  drop: 1,
  bridge: 0.5,
  outro: 0.3,
};

// Map ChordQuality -> scale-degree offsets relative to the chord root.
// Each entry is a list of (scaleStep, octaveShift) tuples; the chord
// root sits at scaleStep 0. Values reach into the next octave when the
// scale only has 7 notes.
type ChordVoicing = Array<{ step: number; oct: number }>;
const VOICINGS: Record<ChordQuality, ChordVoicing> = {
  triad:   [{ step: 0, oct: 0 }, { step: 2, oct: 0 }, { step: 4, oct: 0 }],
  sus2:    [{ step: 0, oct: 0 }, { step: 1, oct: 0 }, { step: 4, oct: 0 }],
  sus4:    [{ step: 0, oct: 0 }, { step: 3, oct: 0 }, { step: 4, oct: 0 }],
  add9:    [{ step: 0, oct: 0 }, { step: 2, oct: 0 }, { step: 4, oct: 0 }, { step: 1, oct: 1 }],
  seventh: [{ step: 0, oct: 0 }, { step: 2, oct: 0 }, { step: 4, oct: 0 }, { step: 6, oct: 0 }],
  power:   [{ step: 0, oct: 0 }, { step: 4, oct: 0 }],
};

export class Composer {
  readonly onBar = new Signal<BarContext>();
  readonly onSectionStart = new Signal<SectionStartEvent>();

  private beat: ProceduralBeat;
  private song: Song | null = null;
  private bar = 0;
  private sectionIdx = 0;
  private sectionBar = 0;
  private active = false;
  // True after start() has fired the priming emit. The next isBarStart
  // beat will be the first REAL beat of bar 0; we suppress its
  // emit-bar so we don't double-fire bar 0. Cleared after that one
  // skipped beat.
  private awaitingFirstBeat = false;
  private unsub: (() => void) | null = null;

  // Cache of the currently playing bar's context so layers that join
  // mid-bar can fetch it (e.g. when MusicDirector unmutes lead during
  // a tier change).
  private currentCtx: BarContext | null = null;

  constructor(beat: ProceduralBeat) {
    this.beat = beat;
  }

  // Start composing for the given song. Resets bar/section counters and
  // emits the first BarContext immediately so layers have a chord to
  // play before the very first kick lands. The priming emit carries
  // time=0 so subscribers can distinguish it from a beat-driven emit
  // and skip time-sensitive scheduling on it.
  start(song: Song): void {
    this.song = song;
    this.bar = 0;
    this.sectionIdx = 0;
    this.sectionBar = 0;
    this.active = true;
    this.awaitingFirstBeat = true;
    if (!this.unsub) {
      this.unsub = this.beat.onStep.on((s) => this.onStep(s));
    }
    this.emitBar(0);
    this.onSectionStart.emit({
      section: this.currentSection().kind,
      bar: this.bar,
      time: 0,
    });
  }

  stop(): void {
    this.active = false;
    this.awaitingFirstBeat = false;
    if (this.unsub) {
      this.unsub();
      this.unsub = null;
    }
    this.song = null;
    this.currentCtx = null;
  }

  isPlaying(): boolean {
    return this.active && this.song !== null;
  }

  getCurrentContext(): BarContext | null {
    return this.currentCtx;
  }

  getCurrentSong(): Song | null {
    return this.song;
  }

  // Notes the lead should play in the bar that contains `globalStep`.
  // Useful for layers that want to schedule per-step instead of per-bar.
  notesForStep(globalStep: number): MotifNote[] {
    if (!this.song || !this.currentCtx) return [];
    const stepInBar = ((globalStep % 16) + 16) % 16;
    const motif = this.motifForCurrentBar();
    if (!motif) return [];
    return motif.filter((n) => n.step === stepInBar);
  }

  // Resolved chord for the current bar (or null if not running).
  getCurrentChord(): ResolvedChord | null {
    return this.currentCtx?.chord ?? null;
  }

  private onStep(step: BeatStep): void {
    if (!this.active || !this.song) return;
    // Advance only when a new bar starts.
    if (!step.isBarStart) return;
    // The very first beat after start() lines up with the priming emit
    // we already fired -- swallow it once so we don't double-emit bar 0.
    if (this.awaitingFirstBeat) {
      this.awaitingFirstBeat = false;
      return;
    }
    this.advanceBar(step.time);
    this.emitBar(step.time);
  }

  private advanceBar(time: number): void {
    if (!this.song) return;
    this.bar += 1;
    this.sectionBar += 1;
    const section = this.currentSection();
    if (this.sectionBar >= section.bars) {
      this.sectionBar = 0;
      this.sectionIdx += 1;
      // Loop back to the first non-intro section once we run out, so
      // the song keeps cycling forever. (Intros only play once.)
      if (this.sectionIdx >= this.song.sections.length) {
        const firstNonIntro = this.song.sections.findIndex(
          (s) => s.kind !== "intro" && s.kind !== "outro"
        );
        this.sectionIdx = firstNonIntro >= 0 ? firstNonIntro : 0;
      }
      this.onSectionStart.emit({
        section: this.currentSection().kind,
        bar: this.bar,
        time,
      });
    }
  }

  private emitBar(time: number): void {
    if (!this.song) return;
    const section = this.currentSection();
    const chordSpec = this.song.progression[
      this.bar % this.song.progression.length
    ]!;
    const chord = this.resolveChord(chordSpec);
    const isFirst = this.sectionBar === 0;
    const isLast = this.sectionBar === section.bars - 1;
    const ctx: BarContext = {
      bar: this.bar,
      sectionBar: this.sectionBar,
      section: section.kind,
      sectionEnergy: SECTION_ENERGY[section.kind],
      chord,
      isFirstBarOfSection: isFirst,
      isLastBarOfSection: isLast,
      nextSection: isLast ? this.peekNextSection() : null,
      time,
    };
    this.currentCtx = ctx;
    this.onBar.emit(ctx);
  }

  private currentSection(): SongSection {
    return this.song!.sections[this.sectionIdx]!;
  }

  private peekNextSection(): SectionKind | null {
    if (!this.song) return null;
    let next = this.sectionIdx + 1;
    if (next >= this.song.sections.length) {
      const firstNonIntro = this.song.sections.findIndex(
        (s) => s.kind !== "intro" && s.kind !== "outro"
      );
      next = firstNonIntro >= 0 ? firstNonIntro : 0;
    }
    return this.song.sections[next]?.kind ?? null;
  }

  private motifForCurrentBar(): MotifNote[] | null {
    if (!this.song || !this.currentCtx) return null;
    const motifs = this.song.motifs[this.currentCtx.section];
    if (!motifs || motifs.length === 0) return null;
    return motifs[this.sectionBar % motifs.length] ?? null;
  }

  // Build a ResolvedChord (bass + chord notes in Hz) from a ChordSpec.
  // Uses the song's scale to keep every chord diatonic.
  private resolveChord(spec: ChordSpec): ResolvedChord {
    const song = this.song!;
    const scaleSemis = SCALES[song.scaleId];
    const scaleLen = scaleSemis.length;
    const root = song.rootHz;
    const rootSemiOffset =
      scaleSemis[((spec.degree % scaleLen) + scaleLen) % scaleLen]!;

    const voicing = VOICINGS[spec.quality];
    const baseSemiAtChordRoot = rootSemiOffset; // the chord's root pitch class in scale terms

    // Mid-register chord notes: aim for ~140-300 Hz which sits above
    // the bass and below the lead.
    const targetMid = 200;
    const baseMidHz = root * Math.pow(2, baseSemiAtChordRoot / 12);
    let midOctaveShift = 0;
    let candidate = baseMidHz;
    while (candidate < targetMid * 0.5) {
      candidate *= 2;
      midOctaveShift += 1;
    }
    while (candidate > targetMid * 2.5) {
      candidate *= 0.5;
      midOctaveShift -= 1;
    }

    const notesHz: number[] = [];
    for (const v of voicing) {
      const idx = ((v.step % scaleLen) + scaleLen) % scaleLen;
      const wrapOctaves = Math.floor(v.step / scaleLen);
      const semis =
        scaleSemis[idx]! - rootSemiOffset + (wrapOctaves + v.oct) * 12;
      const hz = root * Math.pow(2, (rootSemiOffset + semis) / 12);
      notesHz.push(hz * Math.pow(2, midOctaveShift));
    }

    // Bass note: drop the chord root into 35-90 Hz territory unless
    // the spec requests an octave shift.
    let bassHz = root * Math.pow(2, rootSemiOffset / 12);
    while (bassHz > 90) bassHz *= 0.5;
    while (bassHz < 35) bassHz *= 2;
    if (spec.bassOctave) bassHz *= Math.pow(2, spec.bassOctave);

    return {
      degree: spec.degree,
      quality: spec.quality,
      rootHz: candidate,
      bassHz,
      notesHz,
    };
  }

  dispose(): void {
    this.stop();
    this.onBar.clear();
    this.onSectionStart.clear();
  }
}

// Re-export the types so consumers can import everything from one path.
export type {
  BarContext,
  BassRhythm,
  ChordQuality,
  ChordSpec,
  DrumKitConfig,
  MotifNote,
  ResolvedChord,
  SectionKind,
  SectionMotifs,
  Song,
  SongSection,
} from "./types";

// Exposed so song authors / mix code can read intrinsic energy levels
// without re-deriving them.
export { SECTION_ENERGY };
